import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { type PaymentAccountCard } from "@/features/payments/types/payment-account-cards";

import {
  isCardStatusActivationRequired,
  isCardStatusActive,
} from "../../utils/card-status";
import {
  PhysicalCardShippingStatus as ShippingStatus,
  getCardShippingStatus,
} from "../types/physical-card";

import { PhysicalCardActivationFormDialog } from "./physical-card-activation-form-dialog";
import { PhysicalCardShippingSteps } from "./physical-card-shipping-steps";

interface PhysicalCardShippingStatusProps {
  card: PaymentAccountCard;
}

export const PhysicalCardShippingStatus = (
  props: PhysicalCardShippingStatusProps
) => {
  const { card } = props;
  const { t } = useTranslation();

  // Null when PPS sent a status this app does not recognise.
  const deliveryStatus = getCardShippingStatus(card);

  const getStatusMessage = () => {
    if (isCardStatusActive(card.status)) {
      return t("physicalCard_status_message_activated");
    }

    switch (deliveryStatus) {
      case ShippingStatus.NEW:
      case ShippingStatus.SENT_TO_PRINTER:
      case ShippingStatus.APPROVED:
        return t("physicalCard_status_message_ordered");
      case ShippingStatus.PENDING:
        return card.tracking_number
          ? t("physicalCard_status_message_shipped_tracking")
          : t("physicalCard_status_message_shipped");
      case ShippingStatus.SHIPPED:
        return t("physicalCard_status_message_delivered");
      default:
        return "";
    }
  };
  // An unrecognised status says nothing about whether the card has arrived, so
  // the activate action stays open rather than being locked behind a tracker
  // that cannot be drawn.
  const requireActivation =
    deliveryStatus === null ||
    [ShippingStatus.SHIPPED, ShippingStatus.PENDING].includes(deliveryStatus);

  return (
    <Card className="w-full min-h-[240px] md:aspect-[1.55] border-primary/20 bg-white overflow-hidden">
      <div className="flex h-full items-center justify-center p-4 md:p-6">
        <div className="w-full max-w-full overflow-hidden flex flex-col h-full">
          <CardHeader className="mb-4 p-0 md:p-6 md:pt-0">
            <CardTitle className="text-lg md:text-xl">
              {t("delivery_status")}
            </CardTitle>
            <CardDescription>{getStatusMessage()}</CardDescription>
          </CardHeader>
          {deliveryStatus !== null && (
            <div className="flex-1">
              <PhysicalCardShippingSteps deliveryStatus={deliveryStatus} />
            </div>
          )}
          <CardContent className="p-0">
            {isCardStatusActivationRequired(card.status) && (
              <PhysicalCardActivationFormDialog
                card={card}
                disabled={!requireActivation}
              />
            )}
          </CardContent>
        </div>
      </div>
    </Card>
  );
};
